<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { adjustBetsForLimits, BET_ODDS, CHIP_VALUES, formatBalance, formatBetLine, normalizeBalance, sumBets } from '../domain/betting.js';
import GameRoadCanvas from './GameRoadCanvas.vue';
import RoadCanvas from './RoadCanvas.vue';
import BeadRoadCanvas from './BeadRoadCanvas.vue';
import VideoSurface from './VideoSurface.vue';
import AnimatedPoolPanel from './AnimatedPoolPanel.vue';
import CountdownTimer from './CountdownTimer.vue';
import MarqueeText from './MarqueeText.vue';
import { runtimeLogger } from '../services/runtimeLogger.js';
import { calculateBaccaratSettlement } from '../domain/baccaratResult.js';
import { resolveBetEnterAction, sanitizeKeyboardBetValue, saveKeyboardBetDraft } from '../domain/keyboardBetting.js';
import { countdownAudioKey } from '../domain/audioLogic.js';
import { releaseAssetUrl } from '../services/runtimeAssets.js';

const props = defineProps({
  snapshot: { type: Object, required: true },
  service: { type: Object, required: true },
  language: { type: String, default: 'zh' },
  tableIndex: { type: Number, default: 0 },
  dataMode: { type: String, default: 'demo' },
  t: { type: Function, required: true },
  audio: { type: Object, required: true },
  muted: { type: Boolean, default: false },
  mobile: { type: Boolean, default: false },
  initialOverlay: { type: String, default: '' },
  resumeEvent: { type: Object, default: () => ({ id: 0 }) }
});
const emit = defineEmits(['back', 'logout', 'table-change', 'toggle-muted', 'initial-overlay-close']);
const pendingByTable = reactive(Array.from({ length: 4 }, () => [0, 0, 0, 0, 0]));
const initialLocalBets = Array.from({ length: 4 }, (_, index) => props.service.getLocalBetState(index, props.snapshot.tables[index]?.lastSetId));
const placedByTable = reactive(initialLocalBets.map((state) => state.bets));
const historyByTable = reactive(initialLocalBets.map((state) => state.history));
const activeBetIndex = ref(0);
const selectedChipIndex = ref(1);
const zoomed = ref(false);
const mobilePanel = ref('bead');
const mobileTablePanel = ref(false);
const mobileMenuOpen = ref(false);
const roadMode = ref('main');
const askRoadPanel = ref('');
const confirmModel = ref(null);
const overlay = ref('');
const busy = ref(false);
const message = ref('');
const passwordForm = reactive({ oldPassword: '', newPassword: '', confirmPassword: '' });
const report = reactive({ mode: 'total', rows: [], showWashChip: false, loading: false, error: '', page: 1, pageSize: 10, detailDate: '' });
const settlement = ref(null);
const keyboardEntryIndex = ref(-1);
const keyboardDraft = ref('');
const keyboardInputs = [];
const mobileKeyboardInput = ref(null);
let messageTimer = 0;
let roadTimer = 0;
let roadFlashInterval = 0;
let settlementTimer = 0;
let historyAutoCloseTimer = 0;
const SUCCESS_HISTORY_AUTO_CLOSE_MS = 3000;

const table = computed(() => props.snapshot.tables[props.tableIndex] || props.snapshot.tables[0] || {
  displayName: `VIP ${props.tableIndex + 1}`,
  countdown: 0,
  bettingOpen: false,
  boot: 0,
  round: 0,
  pool: {},
  analysis: {}
});
const pending = computed(() => pendingByTable[props.tableIndex] || pendingByTable[0]);
const placed = computed(() => placedByTable[props.tableIndex] || placedByTable[0]);
const history = computed(() => historyByTable[props.tableIndex] || historyByTable[0]);
const limits = computed(() => props.snapshot.user?.limits || {});
const totalPending = computed(() => sumBets(pending.value));
const resultFlashIndexes = computed(() => {
  if (!props.mobile || settlement.value?.tableIndex !== props.tableIndex) return [];
  return settlement.value.zones
    .map((zone, index) => zone.status === 'win' ? index : -1)
    .filter((index) => index >= 0);
});
const displayedRoadMode = computed(() => !props.mobile || askRoadPanel.value === mobilePanel.value ? roadMode.value : 'main');
const reportPageCount = computed(() => Math.max(1, Math.ceil(report.rows.length / report.pageSize)));
const reportPageRows = computed(() => report.rows.slice((report.page - 1) * report.pageSize, report.page * report.pageSize));
const betLabels = computed(() => [
  props.t('vueDesktop.baccarat.banker', 'Banker'),
  props.t('vueDesktop.baccarat.player', 'Player'),
  props.t('vueDesktop.baccarat.tie', 'Tie'),
  props.t('vueDesktop.baccarat.bankerPair', 'Banker Pair'),
  props.t('vueDesktop.baccarat.playerPair', 'Player Pair')
]);
const reportColumns = computed(() => report.mode === 'detail'
  ? [props.t('report.tableNo', 'Table'), props.t('report.time', 'Time'), props.t('report.bet', 'Bet'), props.t('report.winLose', 'Win/Lose'), props.t('report.result', 'Result')]
  : (report.showWashChip
      ? [props.t('report.date', 'Date'), props.t('report.winLose', 'Win/Lose'), props.t('vueDesktop.report.washChip', 'Wash'), props.t('report.betCount', 'Bets')]
      : [props.t('report.date', 'Date'), props.t('report.winLose', 'Win/Lose'), props.t('report.betCount', 'Bets')]));
const desktopChips = [
  { label: '50', imageUrl: '/assets/brand/chips/wanli-chip-50-v2.webp' },
  { label: '100', imageUrl: '/assets/brand/chips/wanli-chip-100-v2.webp' },
  { label: '500', imageUrl: '/assets/brand/chips/wanli-chip-500-v2.webp' },
  { label: '1K', imageUrl: '/assets/brand/chips/wanli-chip-1000-v2.webp' },
  { label: '5K', imageUrl: '/assets/brand/chips/wanli-chip-5000-v2.webp' },
  { label: '10K', imageUrl: '/assets/brand/chips/wanli-chip-10000-v2.webp' },
  { label: '50K', imageUrl: '/assets/brand/chips/wanli-chip-50000-v2.webp' },
  { label: '梭哈', imageUrl: '/assets/brand/chips/wanli-chip-allin-v2.webp' }
].map((chip) => ({ ...chip, imageUrl: releaseAssetUrl(chip.imageUrl) }));
const chips = desktopChips;
const betClasses = ['banker', 'player', 'tie', 'banker-pair', 'player-pair'];
const betEnglish = ['BANKER', 'PLAYER', 'TIE', 'BANKER PAIR', 'PLAYER PAIR'];

function formatNumber(value) {
  return Math.max(0, Number(value) || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function formatSignedNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString('en-US', { maximumFractionDigits: 2 }) : String(value ?? '');
}

function formatSettlementAmount(value) {
  const number = Math.trunc(Number(value) || 0);
  return `${number > 0 ? '+' : ''}${number.toLocaleString('en-US')}`;
}

function formatBootRound(item) {
  return props.t('vueDesktop.format.bootRound', `Boot ${item?.boot ?? 0} · Round ${item?.round ?? 0}`, { boot:item?.boot ?? 0, round:item?.round ?? 0 });
}

function formatReportSession(row) {
  return props.t('report.detailSession', `${row?.SetTime ?? ''} Boot ${row?.BootsNum ?? 0} Round ${row?.SetNum ?? 0}`, {
    time: row?.SetTime ?? '', boot: row?.BootsNum ?? 0, round: row?.SetNum ?? 0
  });
}

function showSettlement(tableIndex, resultCode, resultKey) {
  const result = calculateBaccaratSettlement(resultCode, placedByTable[tableIndex]);
  if (!result) return;
  const winnerLabels = [props.t('vueDesktop.baccarat.bankerWins', 'Banker wins'), props.t('vueDesktop.baccarat.playerWins', 'Player wins'), props.t('vueDesktop.baccarat.tieWins', 'Tie')];
  const headlineSegments = [{ label: winnerLabels[result.winner], category: ['banker', 'player', 'tie'][result.winner] }];
  if (result.bankerPair) headlineSegments.push({ label: props.t('vueDesktop.baccarat.bankerPair', 'Banker Pair'), category: 'banker' });
  if (result.playerPair) headlineSegments.push({ label: props.t('vueDesktop.baccarat.playerPair', 'Player Pair'), category: 'player' });
  settlement.value = { ...result, headlineSegments, tableIndex, resultKey };
  props.audio.playResult(resultCode, result.total);
  window.clearTimeout(settlementTimer);
  settlementTimer = window.setTimeout(() => { settlement.value = null; }, 5000);
}

function parseReportRows(payload) {
  if (Array.isArray(payload)) return payload;
  if (typeof payload !== 'string' || !payload) return [];
  const rows = JSON.parse(payload);
  if (!Array.isArray(rows)) throw new Error(props.t('report.loadError', 'Failed to load data'));
  return rows;
}

function translateReportBetPosition(value) {
  return String(value || '')
    .replace(/庄对|莊對/g, props.t('vueDesktop.baccarat.bankerPair', 'Banker Pair'))
    .replace(/闲对|閒對/g, props.t('vueDesktop.baccarat.playerPair', 'Player Pair'))
    .replace(/庄胜|莊勝|庄|莊/g, props.t('vueDesktop.baccarat.banker', 'Banker'))
    .replace(/闲胜|閒勝|闲|閒/g, props.t('vueDesktop.baccarat.player', 'Player'))
    .replace(/和局|和/g, props.t('vueDesktop.baccarat.tie', 'Tie'))
    .replace(/[.局]/g, ' ').replace(/\s+/g, ' ').trim();
}

function translateReportResult(value) {
  return String(value || '')
    .replace(/庄赢|莊贏/g, props.t('vueDesktop.baccarat.bankerWins', 'Banker wins'))
    .replace(/闲赢|閒贏/g, props.t('vueDesktop.baccarat.playerWins', 'Player wins'))
    .replace(/和局/g, props.t('vueDesktop.baccarat.tieWins', 'Tie'))
    .replace(/庄对|莊對/g, props.t('vueDesktop.baccarat.bankerPair', 'Banker Pair'))
    .replace(/闲对|閒對/g, props.t('vueDesktop.baccarat.playerPair', 'Player Pair'))
    .replace(/\./g, ' ').replace(/\s+/g, ' ').trim();
}

function localizedError(error, fallbackKey, fallback) {
  const raw = String(error?.message || '');
  if (/停止下注|停止投注/.test(raw)) return props.t('message.stopBetting', 'Betting is closed');
  if (/還沒有放置籌碼|还没有放置筹码/.test(raw)) return props.t('message.noChipPlaced', 'You have not placed any chips!');
  if (/餘額不足|余额不足/.test(raw)) return props.t('message.insufficientBalance', 'Insufficient balance');
  if (/結果未確認|结果未确认/.test(raw)) return props.t('vueDesktop.message.betOutcomeUnknown', 'Bet outcome is unknown. Check your balance and records; do not submit again.');
  return props.t(fallbackKey, fallback);
}

function nextReportDate(value) {
  const normalized = String(value || '').trim().replace(/-/g, '/');
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) throw new Error(props.t('report.loadError', 'Failed to load data'));
  date.setDate(date.getDate() + 1);
  return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
}

async function loadReportTotal() {
  props.audio.play('click', { queue: false });
  overlay.value = 'report';
  report.mode = 'total';
  report.detailDate = '';
  report.page = 1;
  report.rows = [];
  report.error = '';
  report.loading = true;
  try {
    const rows = parseReportRows(await props.service.getBetReportTotal());
    report.rows = rows;
    report.showWashChip = !!rows[0]?.isShowWashChip;
  } catch (error) {
    runtimeLogger.error('[wanli-report] total report failed', error);
    report.error = localizedError(error, 'report.loadError', 'Failed to load data');
  } finally {
    report.loading = false;
  }
}

async function loadReportDetail(accountPeriod) {
  props.audio.play('click', { queue: false });
  const startDate = String(accountPeriod || '').trim();
  report.mode = 'detail';
  report.detailDate = startDate;
  report.page = 1;
  report.rows = [];
  report.error = '';
  report.loading = true;
  try {
    const rows = parseReportRows(await props.service.getBetReport(`${startDate} 8:00:00`, `${nextReportDate(startDate)} 8:00:00`));
    report.rows = rows;
  } catch (error) {
    runtimeLogger.error('[wanli-report] detail report failed', error);
    report.error = localizedError(error, 'report.loadError', 'Failed to load data');
  } finally {
    report.loading = false;
  }
}

function showMessage(text, duration = 2600) {
  message.value = text;
  window.clearTimeout(messageTimer);
  messageTimer = window.setTimeout(() => { message.value = ''; }, duration);
}

function clearHistoryAutoClose() {
  window.clearTimeout(historyAutoCloseTimer);
  historyAutoCloseTimer = 0;
}

function showSuccessfulHistory() {
  clearHistoryAutoClose();
  overlay.value = 'history';
  historyAutoCloseTimer = window.setTimeout(() => {
    historyAutoCloseTimer = 0;
    if (overlay.value === 'history') overlay.value = '';
  }, SUCCESS_HISTORY_AUTO_CLOSE_MS);
}

function setKeyboardInputRef(element, index) {
  keyboardInputs[index] = element;
}

function closeKeyboardEntry() {
  keyboardEntryIndex.value = -1;
  keyboardDraft.value = '';
}

function selectBet(index) {
  if (!table.value.bettingOpen) return showMessage(props.t('message.stopBetting', 'Betting is closed'));
  props.audio.play('click', { queue: false });
  if (!props.mobile && keyboardEntryIndex.value >= 0) commitKeyboardEntry();
  activeBetIndex.value = index;
  if (props.mobile) {
    closeKeyboardEntry();
    return;
  }
  keyboardEntryIndex.value = index;
  keyboardDraft.value = pending.value[index] > 0 ? String(pending.value[index]) : '';
  nextTick(() => {
    const input = keyboardInputs[index];
    input?.focus();
    input?.select();
  });
}

function focusMobileKeyboardInput() {
  nextTick(() => {
    mobileKeyboardInput.value?.focus();
    mobileKeyboardInput.value?.select();
  });
}

function openMobileKeyboardBet() {
  if (!table.value.bettingOpen) return showMessage(props.t('message.stopBetting', 'Betting is closed'));
  props.audio.play('click', { queue: false });
  keyboardEntryIndex.value = activeBetIndex.value;
  keyboardDraft.value = pending.value[activeBetIndex.value] > 0 ? String(pending.value[activeBetIndex.value]) : '';
  overlay.value = 'keyboard-bet';
  focusMobileKeyboardInput();
}

function selectMobileKeyboardTarget(index) {
  props.audio.play('click', { queue: false });
  activeBetIndex.value = index;
  keyboardEntryIndex.value = index;
  keyboardDraft.value = pending.value[index] > 0 ? String(pending.value[index]) : '';
  focusMobileKeyboardInput();
}

function applyMobileKeyboardBet() {
  if (!keyboardDraft.value || !commitKeyboardEntry()) {
    props.audio.play('noChipPlaced');
    return showMessage(props.t('message.noChipPlaced', 'You have not placed any chips'));
  }
  props.audio.play('click', { queue: false });
  closeKeyboardEntry();
  overlay.value = '';
}

function updateKeyboardDraft(event) {
  const sanitized = sanitizeKeyboardBetValue(event?.target?.value);
  keyboardDraft.value = sanitized;
  if (event?.target && event.target.value !== sanitized) event.target.value = sanitized;
}

function commitKeyboardEntry() {
  const index = keyboardEntryIndex.value;
  if (index < 0 || index > 4) return false;
  pending.value.splice(0, 5, ...saveKeyboardBetDraft(pending.value, index, keyboardDraft.value));
  return true;
}

function confirmKeyboardEntry() {
  if (!keyboardDraft.value || !commitKeyboardEntry()) return;
  closeKeyboardEntry();
  prepareConfirm();
}

function cancelKeyboardEntry() {
  closeKeyboardEntry();
}

function addChip(index) {
  selectedChipIndex.value = index;
  if (!table.value.bettingOpen) return showMessage(props.t('message.stopBetting', 'Betting is closed'));
  props.audio.play('click', { queue: false });
  const values = pending.value;
  const current = values[activeBetIndex.value] || 0;
  let value = CHIP_VALUES[index];
  if (value === 'all') {
    const otherTotal = values.reduce((sum, item, itemIndex) => itemIndex === activeBetIndex.value ? sum : sum + Number(item || 0), 0);
    value = normalizeBalance(Number(props.snapshot.user?.balance || 0) - otherTotal);
    values[activeBetIndex.value] = value;
  } else {
    const available = normalizeBalance(Number(props.snapshot.user?.balance || 0) - totalPending.value);
    if (available < value) return showMessage(props.t('message.insufficientBalance', 'Insufficient balance'));
    values[activeBetIndex.value] = current + value;
  }
  if (keyboardEntryIndex.value === activeBetIndex.value) keyboardDraft.value = String(values[activeBetIndex.value] || '');
}

function clearPending(playClick = true) {
  if (playClick) props.audio.play('click', { queue: false });
  pending.value.splice(0, 5, 0, 0, 0, 0, 0);
  confirmModel.value = null;
  closeKeyboardEntry();
}

function prepareConfirm(playClick = true) {
  if (playClick) props.audio.play('click', { queue: false });
  try {
    if (keyboardEntryIndex.value >= 0) commitKeyboardEntry();
    closeKeyboardEntry();
    confirmModel.value = adjustBetsForLimits({
      pending: pending.value,
      placed: placed.value,
      limits: limits.value,
      balance: props.snapshot.user?.balance,
      bettingOpen: table.value.bettingOpen
    });
    overlay.value = 'confirm';
  } catch (error) {
    if (/還沒有放置籌碼|还没有放置筹码/.test(String(error?.message || ''))) props.audio.play('noChipPlaced');
    else if (/餘額不足|余额不足/.test(String(error?.message || ''))) props.audio.play('insufficientBalance');
    showMessage(localizedError(error, 'vueDesktop.message.confirmFailed', 'Unable to confirm the bet'));
  }
}

async function submitBet() {
  if (!confirmModel.value || busy.value) return;
  props.audio.play('click', { queue: false });
  busy.value = true;
  try {
    const result = await props.service.placeBet({ tableIndex: props.tableIndex, bets: confirmModel.value.adjusted, seat: 7 });
    const newHistory = result.bets.map((value, index) => value > 0 ? formatBetLine(index, value, betLabels.value[index]) : '').filter(Boolean);
    const local = props.service.recordConfirmedBet(props.tableIndex, table.value.lastSetId, result.bets, newHistory);
    placed.value.splice(0, 5, ...local.bets);
    history.value.splice(0, history.value.length, ...local.history);
    clearPending(false);
    showSuccessfulHistory();
    showMessage(props.t('message.betPlacedSuccess', 'Bet placed successfully!'));
    props.audio.play('betPlacedSuccess', { queue: false });
  } catch (error) {
    if (error?.outcomeUnknown) {
      clearPending(false);
      overlay.value = '';
      showMessage(props.t('vueDesktop.message.betOutcomeUnknown', 'Bet outcome is unknown. Check your balance and records; do not submit again.'), 7000);
    } else {
      showMessage(localizedError(error, 'vueDesktop.message.betFailed', 'Bet failed'));
      props.audio.play('insufficientBalance');
    }
  } finally {
    busy.value = false;
  }
}

function resetAskRoad() {
  window.clearInterval(roadFlashInterval);
  window.clearTimeout(roadTimer);
  roadFlashInterval = 0;
  roadMode.value = 'main';
  askRoadPanel.value = '';
}

function askRoad(mode) {
  props.audio.play('click', { queue: false });
  resetAskRoad();
  let phase = 1;
  askRoadPanel.value = props.mobile ? mobilePanel.value : '';
  roadMode.value = mode;
  roadFlashInterval = window.setInterval(() => {
    phase += 1;
    if (phase >= 12) {
      window.clearInterval(roadFlashInterval);
      roadFlashInterval = 0;
      roadMode.value = 'main';
      askRoadPanel.value = '';
      return;
    }
    roadMode.value = phase % 2 === 1 ? mode : 'main';
  }, 250);
  roadTimer = window.setTimeout(() => {
    window.clearInterval(roadFlashInterval);
    roadFlashInterval = 0;
    roadMode.value = 'main';
    askRoadPanel.value = '';
  }, 3200);
}

function indicator(code, index) {
  const value = String(code || '').toUpperCase();
  if (index === 0) return { shape: 'ring', color: value === 'B' ? 'blue' : 'red' };
  if (index === 1) return { shape: 'dot', color: value === 'D' ? 'blue' : 'red' };
  return { shape: 'slash', color: value === 'F' ? 'blue' : 'red' };
}

function askCodes(side) {
  const raw = table.value.analysis?.WenLuStr;
  const values = Array.isArray(raw) ? raw : String(raw || '').replace(/[\s,|]+/g, '').split('');
  return side === 'banker' ? values.slice(0, 3) : values.slice(3, 6);
}

function selectMobilePanel(panel) {
  props.audio.play('click', { queue: false });
  if (panel !== mobilePanel.value) resetAskRoad();
  mobilePanel.value = panel;
}

function selectMobileTable(index) {
  props.audio.play('click', { queue: false });
  emit('table-change', index);
  mobileTablePanel.value = false;
}

async function submitPassword() {
  props.audio.play('click', { queue: false });
  if (!passwordForm.oldPassword) return showMessage(props.t('message.enterOldPassword', 'Enter your current password'));
  if (!passwordForm.newPassword) return showMessage(props.t('message.enterNewPassword', 'Enter a new password'));
  if (passwordForm.newPassword !== passwordForm.confirmPassword) return showMessage(props.t('message.newPasswordMismatch', 'The new passwords do not match'));
  busy.value = true;
  try {
    const result = await props.service.changePassword(passwordForm.oldPassword, passwordForm.newPassword);
    showMessage(window.AppI18n?.translateMessage?.(result?.message, props.t('message.passwordChangedSuccess', 'Password changed successfully!'), 'message.passwordChangedSuccess') || props.t('message.passwordChangedSuccess', 'Password changed successfully!'));
    overlay.value = '';
    if (props.initialOverlay) emit('initial-overlay-close');
    passwordForm.oldPassword = passwordForm.newPassword = passwordForm.confirmPassword = '';
  } catch (error) {
    showMessage(props.t('vueDesktop.message.passwordChangeFailed', 'Password change failed'));
  } finally {
    busy.value = false;
  }
}

function openOverlay(name) {
  clearHistoryAutoClose();
  props.audio.play('click', { queue: false });
  overlay.value = name;
}

function closeOverlay() {
  clearHistoryAutoClose();
  props.audio.play('click', { queue: false });
  if (overlay.value === 'keyboard-bet') closeKeyboardEntry();
  overlay.value = '';
  if (props.initialOverlay) emit('initial-overlay-close');
}

function handleVideoDoubleClick() {
  if (props.mobile) return;
  zoomed.value = !zoomed.value;
}

function handleKeydown(event) {
  if (event.repeat) return;
  if (event.key === 'Escape') {
    if (overlay.value) {
      clearHistoryAutoClose();
      overlay.value = '';
      if (props.initialOverlay) emit('initial-overlay-close');
    }
    else if (zoomed.value) zoomed.value = false;
    else if (keyboardEntryIndex.value >= 0) cancelKeyboardEntry();
    return;
  }
  if (event.key !== 'Enter' || event.defaultPrevented) return;
  const action = resolveBetEnterAction({
    overlay: overlay.value,
    inputFocused: event.target?.classList?.contains('game-bet-keyboard-input')
  });
  if (action === 'submit') {
    event.preventDefault();
    submitBet();
  } else if (action === 'close-history') {
    event.preventDefault();
    clearHistoryAutoClose();
    props.audio.play('click', { queue: false });
    overlay.value = '';
  }
}

watch(() => props.initialOverlay, (name) => {
  if (name === 'report') void loadReportTotal();
  else if (name === 'password') overlay.value = 'password';
}, { immediate: true });

onMounted(() => document.addEventListener('keydown', handleKeydown));
onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeydown);
  window.clearTimeout(messageTimer);
  window.clearTimeout(roadTimer);
  window.clearInterval(roadFlashInterval);
  window.clearTimeout(settlementTimer);
  clearHistoryAutoClose();
});
watch(() => props.tableIndex, () => {
  clearHistoryAutoClose();
  window.clearInterval(roadFlashInterval);
  window.clearTimeout(roadTimer);
  roadFlashInterval = 0;
  activeBetIndex.value = 0;
  roadMode.value = 'main';
  askRoadPanel.value = '';
  overlay.value = '';
  settlement.value = null;
  closeKeyboardEntry();
});
watch(() => props.snapshot.tables.map((item) => Number(item?.countdown) || 0), (next, previous = []) => {
  next.forEach((value, index) => {
    if (index === props.tableIndex) {
      const audioKey = countdownAudioKey(previous[index], value);
      if (audioKey) props.audio.play(audioKey, { queue: audioKey !== 'tick' });
    }
    if ((previous[index] || 0) > 0 && value <= 0) pendingByTable[index].splice(0, 5, 0, 0, 0, 0, 0);
  });
  if ((previous[props.tableIndex] || 0) > 0 && (next[props.tableIndex] || 0) <= 0) {
    confirmModel.value = null;
    closeKeyboardEntry();
    if (overlay.value === 'confirm' || overlay.value === 'keyboard-bet') overlay.value = '';
  }
});
watch(() => props.snapshot.tables.map((item) => Number(item?.timeStartEventSeq) || 0), (next, previous = []) => {
  next.forEach((eventSeq, index) => {
    if (!eventSeq || eventSeq === (previous[index] || 0) || index !== props.tableIndex) return;
    props.audio.play('startBet');
  });
});
watch(() => props.snapshot.tables.map((item) => String(item?.lastSetId ?? '')), (next, previous = []) => {
  next.forEach((roundKey, index) => {
    if (!roundKey || !previous[index] || roundKey === previous[index]) return;
    const local = props.service.resetLocalBetState(index, roundKey);
    pendingByTable[index].splice(0, 5, 0, 0, 0, 0, 0);
    placedByTable[index].splice(0, 5, ...local.bets);
    historyByTable[index].splice(0, historyByTable[index].length, ...local.history);
    if (settlement.value?.tableIndex === index) settlement.value = null;
  });
});
watch(() => props.snapshot.tables.map((item) => Number(item?.resultEventSeq) || 0), (next, previous = []) => {
  next.forEach((eventSeq, index) => {
    if (!eventSeq || eventSeq === (previous[index] || 0)) return;
    const resultCode = props.snapshot.tables[index]?.resultCode;
    if (resultCode && index === props.tableIndex) {
      resetAskRoad();
      const resultKey = `${props.snapshot.tables[index]?.lastSetId ?? ''}:${resultCode}:${eventSeq}`;
      showSettlement(index, resultCode, resultKey);
    }
  });
});
watch(() => props.resumeEvent?.id, (id, previousId) => {
  if (!id || id === previousId) return;
  props.audio.stopAll();
  if (props.resumeEvent.clearPending) {
    pendingByTable.forEach((values) => values.splice(0, 5, 0, 0, 0, 0, 0));
    confirmModel.value = null;
    overlay.value = '';
    settlement.value = null;
    closeKeyboardEntry();
  }
  if (props.resumeEvent.betOutcomeUnknown) {
    showMessage(props.t('vueDesktop.message.betOutcomeUnknown', 'Bet outcome is unknown. Check your balance and records; do not submit again.'));
  }
}, { immediate: true });
</script>

<template>
  <section class="game-screen vue-game-screen" :class="[{ 'video-zoomed': zoomed && !mobile }, `mobile-panel-${mobilePanel}`]" aria-hidden="false">
    <header class="game-topbar">
      <div class="game-brand"><img src="/assets/brand/wanli-script-gold-v1.webp" alt="Wanli" /><button class="game-back" type="button" @click="emit('back')"><i class="game-top-icon back" aria-hidden="true" /><span>{{ t('button.back', 'Back') }}</span></button></div>
      <MarqueeText class="game-marquee" :label="t('vueDesktop.label.notice', 'Notice')" :text="snapshot.news" />
      <nav class="game-top-actions"><button class="game-top-action" type="button" @click="loadReportTotal"><i class="game-top-icon record" aria-hidden="true" /><span>{{ t('buttonCompact.betRecord', 'Record') }}</span></button><button class="game-top-action" type="button" @click="openOverlay('password')"><i class="game-top-icon key" aria-hidden="true" /><span>{{ t('buttonCompact.changePassword', 'Password') }}</span></button><button class="game-top-action game-exit" type="button" @click="emit('logout')"><i class="game-top-icon exit" aria-hidden="true" /><span>{{ t('button.exitGame', 'Exit') }}</span></button></nav>
      <button class="mobile-game-menu-toggle" type="button" :aria-label="t('vueDesktop.label.tableInfo','Menu')" :aria-expanded="String(mobileMenuOpen)" @click="mobileMenuOpen=!mobileMenuOpen"><i aria-hidden="true" /><span>{{ t('vueDesktop.label.tableInfo','Menu') }}</span></button>
      <div v-if="mobileMenuOpen" class="mobile-game-menu">
        <button type="button" @click="mobileMenuOpen=false;loadReportTotal()">{{ t('buttonCompact.betRecord','Record') }}</button>
        <button type="button" @click="mobileMenuOpen=false;openOverlay('password')">{{ t('buttonCompact.changePassword','Password') }}</button>
        <button type="button" @click="emit('logout')">{{ t('button.exitGame','Exit') }}</button>
      </div>
    </header>
    <main class="game-main">
      <section class="game-video-shell">
        <div class="game-video-frame" @dblclick="handleVideoDoubleClick">
          <VideoSurface v-if="!initialOverlay" :key="`video-table-${tableIndex}`" :service="service" :table-index="tableIndex" :data-mode="dataMode" :resume-event="resumeEvent" :t="t" />
          <AnimatedPoolPanel :pool="table.pool" :t="t" />
          <div class="game-video-limit"><div class="game-limit-title">{{ t('vueDesktop.label.tableLimit', 'Table Limit') }}</div><div class="game-limit-row"><span>{{ t('vueDesktop.baccarat.banker', 'Banker') }} / {{ t('vueDesktop.baccarat.player', 'Player') }}</span><b>{{ formatNumber(limits.mainMin) }} - {{ formatNumber(limits.mainMax) }}</b></div><div class="game-limit-row"><span>{{ t('vueDesktop.baccarat.tie', 'Tie') }}</span><b>{{ formatNumber(limits.tieMin) }} - {{ formatNumber(limits.tieMax) }}</b></div><div class="game-limit-row"><span>{{ t('vueDesktop.label.pairs', 'Pairs') }}</span><b>{{ formatNumber(limits.pairMin) }} - {{ formatNumber(limits.pairMax) }}</b></div></div>
          <div class="game-video-caption"><strong>{{ table.displayName }}</strong><span>{{ formatBootRound(table) }}</span></div>
          <CountdownTimer :seconds="table.countdown" :t="t" />
          <button class="mobile-change-table" type="button" @click.stop="mobileTablePanel=true">{{ t('button.selectTable','Select table') }}</button>
          <button class="mobile-keyboard-bet-button" type="button" @click.stop="openMobileKeyboardBet">{{ t('vueDesktop.button.keyboardBet','Keyboard Bet') }}</button>
          <button class="game-video-sound" :class="{ muted }" type="button" :aria-label="muted?t('vueDesktop.button.unmute','Unmute'):t('vueDesktop.button.mute','Mute')" :aria-pressed="String(muted)" @click.stop="emit('toggle-muted')"><i class="sound-control-icon" aria-hidden="true" /></button>
        </div>
      </section>
      <aside class="game-side">
        <div class="game-summary"><section class="game-summary-card"><div class="game-account"><span class="game-account-id"><small>{{ t('vueDesktop.label.account', 'Account') }}</small><strong>{{ snapshot.user?.name }}</strong></span><span class="game-account-balance"><small>{{ t('vueDesktop.label.balance', 'Balance') }}</small><b>{{ formatBalance(snapshot.user?.balance) }}</b></span></div></section></div>
        <div class="game-chips"><button v-for="(chip,index) in chips" :key="chip.label" class="game-chip" :class="{ active:selectedChipIndex===index }" :style="{ '--chip-static':`url(${chip.imageUrl})` }" type="button" :aria-label="chip.label==='梭哈'?t('vueDesktop.label.allIn','All in'):chip.label" @click="addChip(index)"><span>{{ chip.label==='梭哈'?t('vueDesktop.label.allIn','All in'):chip.label }}</span></button></div>
        <div class="game-bets">
          <button v-for="(key,index) in betClasses" :key="key" class="game-bet" :class="[key,{ 'bet-label-zh':language==='zh', selected:activeBetIndex===index && table.bettingOpen, 'result-flash':resultFlashIndexes.includes(index), 'keyboard-editing':!mobile && keyboardEntryIndex===index, closed:!table.bettingOpen }]" type="button" :aria-pressed="String(activeBetIndex===index && table.bettingOpen)" @click="selectBet(index)"><em>{{ betEnglish[index] }}</em><strong>{{ betLabels[index] }}</strong><small>{{ t('vueDesktop.label.payout', 'Pays') }} {{ BET_ODDS[index] }} : 1</small><input v-if="!mobile && keyboardEntryIndex===index" :ref="element=>setKeyboardInputRef(element,index)" class="game-bet-keyboard-input" :value="keyboardDraft" type="text" inputmode="numeric" autocomplete="off" :aria-label="`${betLabels[index]} ${t('report.bet','Bet')}`" @click.stop @input="updateKeyboardDraft" @keydown.enter.stop.prevent="confirmKeyboardEntry" @keydown.escape.stop.prevent="cancelKeyboardEntry" /><span v-if="placed[index]" class="game-bet-amount confirmed">{{ formatNumber(placed[index]) }}</span><span v-if="pending[index] && (!mobile && keyboardEntryIndex!==index || mobile)" class="game-bet-amount pending">{{ formatNumber(pending[index]) }}</span></button>
        </div>
        <div class="game-confirm-row" :class="{ 'has-pending':totalPending>0 && !overlay }"><button class="game-confirm-button primary" type="button" @click="prepareConfirm"><i aria-hidden="true">✓</i><span><b>{{ t('button.confirm', 'Confirm') }}</b><small>CONFIRM BET</small></span></button><button class="game-confirm-button secondary" type="button" @click="clearPending"><i aria-hidden="true">×</i><span><b>{{ t('button.cancel', 'Cancel') }}</b><small>CANCEL BET</small></span></button></div>
        <div class="game-controls"><button class="game-control slip" type="button" @click="openOverlay('history')"><i class="slip-control-icon" aria-hidden="true" /><span><b>{{ t('button.betSlip', 'Bet Slip') }}</b><small>BET SLIP</small></span></button><button class="game-control ask-road banker-road" :class="{ active:roadMode==='banker' }" type="button" @click="askRoad('banker')"><span class="ask-road-label"><b>{{ t('vueDesktop.baccarat.bankerAsk', 'Banker Ask') }}</b><small>BANKER ASK</small></span><span class="ask-road-result"><span v-for="(code,index) in askCodes('banker')" :key="index"><i :class="[`ask-${indicator(code,index).shape}`,indicator(code,index).color]" /></span></span></button><button class="game-control ask-road player-road" :class="{ active:roadMode==='player' }" type="button" @click="askRoad('player')"><span class="ask-road-label"><b>{{ t('vueDesktop.baccarat.playerAsk', 'Player Ask') }}</b><small>PLAYER ASK</small></span><span class="ask-road-result"><span v-for="(code,index) in askCodes('player')" :key="index"><i :class="[`ask-${indicator(code,index).shape}`,indicator(code,index).color]" /></span></span></button></div>
      </aside>
    </main>
    <footer class="game-road-shell"><nav class="game-table-tabs"><button v-for="item in snapshot.tables" :key="item.id" class="game-tab" :class="{ active:item.index===tableIndex }" type="button" @click="selectMobileTable(item.index)"><span class="game-tab-main"><small>TABLE {{ String(item.index+1).padStart(2,'0') }}</small><b>{{ item.displayName }}</b></span><span class="game-tab-side"><i /><b>{{ item.countdown }}</b><small>SEC</small></span></button></nav><div class="game-road-layout"><template v-if="mobile"><BeadRoadCanvas v-if="mobilePanel==='bead'" :analysis="table.analysis" :mode="displayedRoadMode" :table-key="tableIndex" :flash-seq="table.resultEventSeq" :t="t" /><RoadCanvas v-else :analysis="table.analysis" :mode="displayedRoadMode" :seed="tableIndex+1" :table-key="tableIndex" :flash-seq="table.resultEventSeq" /></template><GameRoadCanvas v-else :analysis="table.analysis" :mode="displayedRoadMode" :seed="tableIndex+1" :table-key="tableIndex" :flash-seq="table.resultEventSeq" :t="t" /><Transition name="wanli-result"><div v-if="settlement" class="game-result-overlay" :class="settlement.total>0?'positive':settlement.total<0?'negative':'neutral'"><strong class="game-result-headline"><template v-for="(segment,index) in settlement.headlineSegments" :key="`${segment.category}-${index}`"><i v-if="index" aria-hidden="true">·</i><span :class="`result-${segment.category}`">{{ segment.label }}</span></template></strong><b>{{ formatSettlementAmount(settlement.total) }}</b></div></Transition></div></footer>

    <nav class="mobile-game-nav" :aria-label="t('vueDesktop.label.tableInfo','Game panels')">
      <button type="button" :class="{ active:mobilePanel==='bead' }" @click="selectMobilePanel('bead')"><span>{{ t('button.roadPearl','Beads') }}</span></button>
      <button type="button" :class="{ active:mobilePanel==='road' }" @click="selectMobilePanel('road')"><span>{{ t('button.bigRoad','Big Road') }}</span></button>
    </nav>

    <div v-if="mobileTablePanel" class="mobile-table-panel" @click.self="mobileTablePanel=false">
      <section>
        <button v-for="item in snapshot.tables" :key="item.id" type="button" :class="{ active:item.index===tableIndex }" @click="selectMobileTable(item.index)">
          <header><b>{{ item.displayName }}</b><span>{{ item.countdown }}</span></header>
          <RoadCanvas :analysis="item.analysis" :seed="item.index+1" />
        </button>
      </section>
    </div>

    <div v-if="overlay" class="vue-game-overlay" @click.self="closeOverlay">
      <section v-if="overlay==='keyboard-bet'" class="vue-game-dialog vue-game-keyboard-bet-dialog">
        <header><span>KEYBOARD BET</span><h2>{{ t('vueDesktop.button.keyboardBet','Keyboard Bet') }}</h2></header>
        <div class="mobile-keyboard-bet-targets"><button v-for="(label,index) in betLabels" :key="index" type="button" :class="[betClasses[index],{active:keyboardEntryIndex===index}]" @click="selectMobileKeyboardTarget(index)">{{ label }}</button></div>
        <label class="mobile-keyboard-bet-amount"><span>{{ betLabels[keyboardEntryIndex>=0?keyboardEntryIndex:activeBetIndex] }} · {{ t('report.bet','Bet') }}</span><input ref="mobileKeyboardInput" :value="keyboardDraft" type="text" inputmode="numeric" pattern="[0-9]*" autocomplete="off" @input="updateKeyboardDraft" @keydown.enter.stop.prevent="applyMobileKeyboardBet" /></label>
        <footer><button type="button" class="primary" @click="applyMobileKeyboardBet">{{ t('button.confirm','Confirm') }}</button><button type="button" @click="closeOverlay">{{ t('button.cancel','Cancel') }}</button></footer>
      </section>
      <section v-else-if="overlay==='confirm'" class="vue-game-dialog vue-game-confirm-dialog">
        <header><span>CONFIRM BET</span><h2>{{ t('title.confirmBet', 'Confirm Bet') }}</h2></header>
        <div class="vue-confirm-list"><div v-for="(value,index) in confirmModel?.adjusted" :key="index" :class="{ adjusted:confirmModel?.changed[index] }"><span>{{ betLabels[index] }}</span><b>{{ formatNumber(value) }}</b></div></div>
        <div class="vue-confirm-total"><span>{{ t('label.total', 'Total') }}</span><b>{{ formatNumber(confirmModel?.total) }}</b></div>
        <p v-if="confirmModel?.changed.some(Boolean)">{{ t('title.limitAdjusted', 'Bets were adjusted to the table limit') }}</p>
        <p class="vue-confirm-keyboard-hint">{{ t('vueDesktop.message.keyboardConfirmHint', 'Press Enter again to place the bet · Esc to edit') }}</p>
        <footer><button type="button" class="primary" :disabled="busy" @click="submitBet">{{ busy?t('vueDesktop.status.processing','Processing'):t('button.confirm','Confirm') }}</button><button type="button" @click="closeOverlay">{{ t('vueDesktop.button.returnModify', 'Edit Bet') }}</button></footer>
      </section>
      <section v-else-if="overlay==='history'" class="vue-game-dialog vue-game-history-dialog">
        <header><span>BET SLIP</span><h2>{{ t('vueDesktop.title.tableBetSlip', 'Table Bet Slip') }}</h2></header>
        <div class="vue-history-list"><p v-if="!history.length">{{ t('vueDesktop.message.noConfirmedBets', 'No confirmed bets for this table') }}</p><div v-for="(line,index) in history" :key="index">{{ line }}</div></div>
        <footer><button type="button" @click="closeOverlay">{{ t('vueDesktop.button.close', 'Close') }}</button></footer>
      </section>
      <section v-else-if="overlay==='report'" class="vue-game-dialog vue-game-report-dialog">
        <header><span>BETTING HISTORY REPORT</span><h2>{{ report.mode==='total' ? t('vueDesktop.title.betHistoryReport','Bet History Report') : t('vueDesktop.title.betDetail','Bet Details') }}</h2></header>
        <div class="vue-report-toolbar"><button v-if="report.mode==='detail'" type="button" @click="loadReportTotal">‹ {{ t('report.backToDates', 'Back to Dates') }}</button><span v-if="report.detailDate">{{ report.detailDate }}</span><small>{{ t('vueDesktop.label.account', 'Account') }} {{ snapshot.user?.name }}</small></div>
        <div v-if="report.loading" class="vue-report-loading"><i /><span>{{ t('report.loading', 'Loading') }}</span></div>
        <div v-else-if="report.error" class="vue-report-empty error">{{ report.error }}</div>
        <div v-else-if="!report.rows.length" class="vue-report-empty">{{ t('report.noRecords', 'No records found') }}</div>
        <div v-else class="vue-report-table-wrap">
          <table class="vue-report-table" :class="{ detail:report.mode==='detail', total:report.mode==='total' }">
            <thead><tr><th v-for="column in reportColumns" :key="column">{{ column }}</th></tr></thead>
            <tbody>
              <tr v-for="(row,index) in reportPageRows" :key="index" :class="{ clickable:report.mode==='total' }" @click="report.mode==='total' && loadReportDetail(row.AccountPeriod)">
                <template v-if="report.mode==='total'">
                  <td><button type="button" class="vue-report-date">{{ row.AccountPeriod }}</button></td><td :class="{ positive:Number(row.WinLose)>0,negative:Number(row.WinLose)<0 }">{{ formatSignedNumber(row.WinLose) }}</td><td v-if="report.showWashChip">{{ formatSignedNumber(row.WashChip) }}</td><td>{{ formatSignedNumber(row.BetCounts) }}</td>
                </template>
                <template v-else>
                  <td>{{ row.DesktopName }}</td><td>{{ formatReportSession(row) }}</td><td>{{ translateReportBetPosition(row.BetPosition) }}　{{ formatSignedNumber(row.BetValue) }}</td><td :class="{ positive:Number(row.WinLose)>0,negative:Number(row.WinLose)<0 }">{{ formatSignedNumber(row.WinLose) }}</td><td>{{ translateReportResult(row.Results) }}</td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="!report.loading && !report.error && report.rows.length" class="vue-report-pagination"><span>{{ t('vueDesktop.report.count',`${report.rows.length} records`,{count:report.rows.length}) }}</span><button type="button" :disabled="report.page<=1" @click="report.page--">{{ t('report.paginationPrevious', 'Previous') }}</button><b>{{ report.page }} / {{ reportPageCount }}</b><button type="button" :disabled="report.page>=reportPageCount" @click="report.page++">{{ t('report.paginationNext', 'Next') }}</button></div>
        <footer><button type="button" @click="closeOverlay">{{ t('vueDesktop.button.closeReport', 'Close Report') }}</button></footer>
      </section>
      <form v-else class="vue-game-dialog vue-game-password-dialog" @submit.prevent="submitPassword">
        <header><span>SECURITY</span><h2>{{ t('button.changePassword', 'Change Password') }}</h2></header>
        <label>{{ t('label.oldPassword', 'Current Password') }}<input v-model="passwordForm.oldPassword" type="password" /></label><label>{{ t('label.newPassword', 'New Password') }}<input v-model="passwordForm.newPassword" type="password" /></label><label>{{ t('label.confirmNewPassword', 'Confirm New Password') }}<input v-model="passwordForm.confirmPassword" type="password" /></label>
        <footer><button type="submit" class="primary" :disabled="busy">{{ busy?t('vueDesktop.status.processing','Processing'):t('button.confirm','Confirm') }}</button><button type="button" @click="closeOverlay">{{ t('button.cancel', 'Cancel') }}</button></footer>
      </form>
    </div>
    <div v-if="message" class="vue-game-toast" role="status">{{ message }}</div>
  </section>
</template>
